'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { getStoredAIReferrer } from '@/lib/analytics/ai-referrer-tracker'
import { peekVisitorId } from '@/lib/analytics/visitor-id'
import { getFirstTouchUtm } from '@/lib/analytics/first-touch-utm'
import { trackEvent } from '@/lib/track-event'
import { isAnalyticsConsentGranted } from '@/lib/consent-manager'

/**
 * Connects the reference contact form (#cform) to the live /api/contact
 * endpoint with the same payload, attribution and conversion events the
 * previous form sent. The reference script calls window.__pmContact(form).
 *
 * contact_form_started fires as the previous form fired it (contact-client
 * markStarted): at most once per form instance, on the first real edit, a
 * field change or a subject chip click. Never on mount or focus; the
 * script's own chip click only runs after a submit, when it already fired
 * (Qodo, PR #390: without it the contact funnel lost its start step).
 */
export default function ContactBridge() {
  const pathname = usePathname()
  useEffect(() => {
    const form = document.getElementById('cform')
    const chips = document.getElementById('subj')
    let started = false
    const markStarted = () => {
      if (started) return
      started = true
      trackEvent('contact_form_started', {
        form_id: 'contact_us',
        page: pathname,
      })
    }
    form?.addEventListener('input', markStarted)
    chips?.addEventListener('click', markStarted)
    return () => {
      form?.removeEventListener('input', markStarted)
      chips?.removeEventListener('click', markStarted)
    }
  }, [pathname])

  useEffect(() => {
    const w = window as unknown as {
      __pmContact?: (fd: FormData) => Promise<void>
    }
    w.__pmContact = async (fd: FormData) => {
      const firstTouchUtm = isAnalyticsConsentGranted()
        ? (getFirstTouchUtm() ?? {})
        : {}
      const ref = getStoredAIReferrer()
      const aiReferrerSource = ref?.isAIReferred ? ref.source : ''
      const aiReferrerURL = ref?.isAIReferred ? ref.referrer : ''
      const field = (k: string) => String(fd.get(k) ?? '').trim()
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: field('name'),
          email: field('email'),
          subject: field('subject'),
          budget: field('budget'),
          message: field('message'),
          aiReferrerSource,
          aiReferrerURL,
          ...firstTouchUtm,
          visitorId: peekVisitorId(),
        }),
      })
      const data = (await response.json().catch(() => ({}))) as {
        message?: string
      }
      if (!response.ok) {
        throw new Error(
          data.message || 'We could not send your message. Please try again.'
        )
      }
      trackEvent('contact_form_submitted', {
        form_id: 'contact_us',
        page: pathname,
        ...firstTouchUtm,
      })
      const gtag = (window as unknown as { gtag?: (...a: unknown[]) => void })
        .gtag
      if (aiReferrerSource && isAnalyticsConsentGranted() && gtag) {
        gtag('event', 'ai_referred_form_submission', {
          ai_source: aiReferrerSource,
          ai_referrer: aiReferrerURL,
          event_category: 'agentic_commerce',
          event_label: `AI Form Submit: ${aiReferrerSource}`,
          value: 1,
        })
      }
    }
    return () => {
      delete w.__pmContact
    }
  }, [pathname])
  return null
}
